import { useState, useEffect, useRef } from 'react';

/**
 * Single source for exam countdown. Used by ExamRoom and StaffDashboard.
 * Clears interval on unmount.
 * @param {boolean} examStarted
 * @param {number|null} examEndTime - timestamp (ms)
 * @param {number} serverOffset - client-server clock offset (ms)
 * @returns { { timeRemaining: number, formatTime: (s: number) => string } }
 */
export function useExamTimer(examStarted, examEndTime, serverOffset = 0) {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const intervalRef = useRef(null);

  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!examStarted || !examEndTime) {
      setTimeRemaining(0);
      return;
    }
    const updateCountdown = () => {
      const now = Date.now() + serverOffset;
      const diff = Math.floor((examEndTime - now) / 1000);
      setTimeRemaining(diff <= 0 ? 0 : diff);
    };
    updateCountdown();
    intervalRef.current = setInterval(updateCountdown, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [examStarted, examEndTime, serverOffset]);

  return { timeRemaining, formatTime };
}

/**
 * Parses exam date and time into start and end epoch timestamps (ms).
 * Handles DD-MM-YYYY, YYYY-MM-DD, single time strings ("10:45 AM", "10:45:00"),
 * and time range strings ("10:45 AM - 01:45 PM (1)").
 */
export function parseExamSchedule(examDate, examTime, durationMinutes = null) {
  if (!examDate) return null;

  // 1. Parse Date (YYYY-MM-DD or DD-MM-YYYY or standard)
  const dStr = String(examDate).trim();
  const dateSep = dStr.includes('-') ? '-' : (dStr.includes('/') ? '/' : null);
  let year, month, day;
  if (dateSep) {
    const parts = dStr.split(dateSep).map(p => parseInt(p, 10));
    if (parts.length === 3) {
      if (parts[0] > 1000) {
        // YYYY-MM-DD
        [year, month, day] = parts;
      } else if (parts[2] > 1000) {
        // DD-MM-YYYY
        [day, month, year] = parts;
      }
    }
  }

  if (!year || !month || !day) {
    const parsed = new Date(dStr);
    if (!isNaN(parsed.getTime())) {
      year = parsed.getFullYear();
      month = parsed.getMonth() + 1;
      day = parsed.getDate();
    }
  }

  if (!year || !month || !day) return null;

  // 2. Parse Time (support ranges like "10:45 AM - 01:45 PM" or single time "10:45:00")
  if (!examTime) return null;
  const tStr = String(examTime).trim();
  const rangeMatch = tStr.match(/(.+?)\s*-\s*(.+)/);
  const startTimeStr = rangeMatch ? rangeMatch[1].trim() : tStr;
  const endTimeStr = rangeMatch ? rangeMatch[2].trim() : null;

  const parseTimeComponent = (str) => {
    if (!str) return null;
    const match = str.match(/(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?/i);
    if (!match) return null;
    let [ , hStr, mStr, sStr, meridiem ] = match;
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    const s = sStr ? parseInt(sStr, 10) : 0;
    if (meridiem) {
      const upper = meridiem.toUpperCase();
      if (upper === 'PM' && h < 12) h += 12;
      if (upper === 'AM' && h === 12) h = 0;
    }
    return { h, m, s };
  };

  const startComp = parseTimeComponent(startTimeStr);
  if (!startComp) return null;

  const startMs = new Date(year, month - 1, day, startComp.h, startComp.m, startComp.s).getTime();
  if (isNaN(startMs)) return null;

  let endMs = null;
  const parsedDuration = (durationMinutes != null && !isNaN(Number(durationMinutes)) && Number(durationMinutes) > 0)
    ? parseInt(durationMinutes, 10)
    : null;

  if (parsedDuration) {
    endMs = startMs + (parsedDuration * 60 * 1000);
  } else if (endTimeStr) {
    const endComp = parseTimeComponent(endTimeStr);
    if (endComp) {
      endMs = new Date(year, month - 1, day, endComp.h, endComp.m, endComp.s).getTime();
      if (endMs < startMs) endMs += 24 * 3600 * 1000;
    }
  }

  return { startMs, endMs };
}

export function calculateLiveEndTime(durationMinutes, startTimestamp = null, examDate = null, examTime = null) {
  // 1. Scheduled start & end time takes primary precedence for the exam window
  if (examDate && examTime) {
    const schedule = parseExamSchedule(examDate, examTime, durationMinutes);
    if (schedule?.endMs) {
      return schedule.endMs;
    }
  }

  if (!durationMinutes) return null;
  const durationMs = parseInt(durationMinutes, 10) * 60 * 1000;

  // 2. Fallback to startTimestamp + duration if no scheduled exam date & time
  if (startTimestamp && !isNaN(Number(startTimestamp))) {
    return Number(startTimestamp) + durationMs;
  }

  return Date.now() + durationMs;
}


